import { useState, useEffect } from 'react';
import { History, MapPin, Trash2, ChevronDown, ChevronUp, CheckCircle, AlertCircle, Lock } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { isIOS } from '../lib/capacitor';
import { API_URL as BACKEND_URL } from '../lib/apiUrl';
import './SearchHistory.css';

// Check if tier is a paid subscription
const isSubscriber = (tier) => ['starter', 'starter_annual', 'pro', 'pro_annual'].includes(tier);

export default function SearchHistory({ onSelectPlate }) {
  const { isAuthenticated, getAccessToken, tier } = useAuth();
  const toast = useToast();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const hasSubscription = isSubscriber(tier);

  useEffect(() => {
    if (isAuthenticated && expanded && hasSubscription) {
      fetchHistory();
    }
  }, [isAuthenticated, expanded, hasSubscription]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const token = await getAccessToken();
      const response = await fetch(`${BACKEND_URL}/api/search-history?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch history');
      setHistory(data.history || []);
    } catch (err) {
      console.error('Failed to fetch history:', err);
    } finally {
      setLoading(false);
    }
  };

  const deleteEntry = async (id, e) => {
    e.stopPropagation();
    try {
      const token = await getAccessToken();
      const response = await fetch(`${BACKEND_URL}/api/search-history/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        throw new Error('Delete failed');
      }
      setHistory(prev => prev.filter(h => h.id !== id));
      toast.success('Removed from history');
    } catch (err) {
      toast.error('Could not delete entry');
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (!isAuthenticated) return null;
  // On iOS, only show search history to actual subscribers — no upgrade teaser.
  if (isIOS() && !hasSubscription) return null;

  return (
    <div className="search-history">
      <button
        className="history-toggle"
        onClick={() => setExpanded(!expanded)}
      >
        <History size={16} />
        <span>Recent Searches</span>
        {!hasSubscription && <Lock size={14} className="lock-icon" />}
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {expanded && (
        <div className="history-list">
          {!hasSubscription ? (
            <div className="history-upgrade">
              <Lock size={20} />
              <p>Search history is a subscriber feature</p>
              <Link to="/pricing" className="upgrade-link">Upgrade to unlock</Link>
            </div>
          ) : loading ? (
            <div className="history-loading">Loading...</div>
          ) : history.length === 0 ? (
            <div className="history-empty">
              <History size={24} />
              <p>No searches yet</p>
              <span>Scan or enter a plate to get started</span>
            </div>
          ) : (
            history.map(entry => (
              <div
                key={entry.id}
                className="history-entry"
                onClick={() => onSelectPlate(entry.plate, entry.state, entry)}
              >
                <div className="history-main">
                  <div className="history-plate">
                    {entry.was_licensed ? (
                      <CheckCircle size={14} className="status-icon licensed" />
                    ) : (
                      <AlertCircle size={14} className="status-icon not-licensed" />
                    )}
                    <span className="plate-text">{entry.plate}</span>
                    {entry.vehicle_info && (
                      <span className="vehicle-brief">
                        {entry.vehicle_info.year} {entry.vehicle_info.make}
                      </span>
                    )}
                  </div>
                  <div className="history-meta">
                    {entry.location_name && (
                      <span className="history-location">
                        <MapPin size={12} />
                        {entry.location_name}
                      </span>
                    )}
                    <span className="history-time">{formatDate(entry.searched_at)}</span>
                  </div>
                </div>
                <button
                  className="history-delete"
                  onClick={(e) => deleteEntry(entry.id, e)}
                  title="Remove from history"
                  aria-label="Remove from history"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
